<div class="keep-shell resume-shell">
  <!-- HEADER / SEARCH --------------------------------------------------- -->
  <header class="keep-header">
    <div class="brand">
      <img height="49" src="assets/svg/file-attach.svg" alt="Resume icon" />
      <span class="page-title">Resume Revival Robot</span>
    </div>

    <form [formGroup]="searchForm" class="search">
      <i class="fa fa-search search-icon"></i>
      <input
        formControlName="query"
        placeholder="Search title, label, or any text…"
      />
    </form>

    <div class="header-actions">
      <button
        type="button"
        class="header-btn"
        (click)="toggleArchivedView()
        "
      >
        <i
          class="fa"
          [ngClass]="showArchived ? 'fa-box-open' : 'fa-box-archive'"
        >
        </i>
        <span>{{ showArchived ? 'Show active' : 'Show archived' }}</span>
      </button>
    </div>
  </header>

  <!-- LABEL FILTERS ----------------------------------------------------- -->
  <section class="labels" *ngIf="labels.length">
    <button
      type="button"
      class="label-chip-btn"
      [class.active]="!selectedLabel"
      (click)="selectLabel(null)"
    >
      All
    </button>

    <button
      type="button"
      class="label-chip-btn"
      *ngFor="let label of labels"
      [class.active]="label === selectedLabel"
      (click)="selectLabel(label)"
    >
      {{ label }}
    </button>
  </section>

  <!-- NEW RESUME INPUT + JOB TARGETING --------------------------------- -->
  <section class="new-resume">
    <div class="new-resume-layout">
      <!-- LEFT: UPLOAD / ATTACH FILE CARD ------------------------------- -->
      <form [formGroup]="resumeForm" (ngSubmit)="createResume()">
        <div
          class="new-resume-card"
          [style.backgroundColor]="uploadCardColor"
        >
          <div class="new-resume-main">
            <input
              type="text"
              class="title"
              placeholder="Resume title (e.g. Senior AI Engineer)"
              formControlName="title"
            />

            <textarea
              class="description"
              placeholder="Internal notes about this resume (candidate, context, goals)…"
              formControlName="description"
              rows="3"
            >
            </textarea>

            <div class="meta-row">
              <div class="field-group">
                <label>Primary category</label>
                <select formControlName="primaryCategory">
                  <option *ngFor="let c of primaryCategories" [value]="c">
                    {{ c }}
                  </option>
                </select>
              </div>

              <div class="field-group">
                <label>Labels</label>
                <input
                  type="text"
                  placeholder="Comma-separated labels (e.g. Python, Remote, EU)"
                  formControlName="extraLabels"
                />
              </div>
            </div>
          </div>

          <!-- RIGHT SIDE: FILE PICKER + THUMBNAIL + CONTROLS ------------ -->
          <div class="new-resume-side">
            <div class="file-row">
              <label class="file-input-button">
                <input
                  type="file"
                  (change)="onFileSelected($event)"
                  accept=".pdf,.doc,.docx,.txt"
                  hidden
                />
                <i class="fa fa-upload"></i>
                <span>
                  {{ selectedFile ? 'Change file' : 'Attach resume' }}
                </span>
              </label>

              <div class="file-preview" *ngIf="selectedFile">
                <div class="file-thumb">
                  <div class="file-thumb-box">
                    {{ selectedFileExt }}
                  </div>
                </div>
                <div class="file-info">
                  <div class="file-name" [title]="selectedFile.name">
                    {{ selectedFile.name }}
                  </div>
                  <div class="file-size">
                    {{ selectedFileSizeKb }} KB
                  </div>
                </div>
              </div>
            </div>

            <div class="controls-row">
              <div class="controls-left">
                <button
                  type="button"
                  class="icon-toggle"
                  [class.active]="resumeForm.value.pinned"
                  (click)="resumeForm.patchValue({ pinned: !resumeForm.value.pinned })"
                  title="Pin this resume"
                >
                  <i class="fa fa-thumbtack"></i>
                </button>

                <div class="color-dots">
                  <button
                    type="button"
                    *ngFor="let c of colorPalette"
                    class="color-dot"
                    [style.backgroundColor]="c"
                    [class.selected]="resumeForm.value.color === c"
                    (click)="resumeForm.patchValue({ color: c })"
                  >
                  </button>
                </div>
              </div>

              <div class="controls-right">
                <button
                  type="submit"
                  class="primary-btn"
                  [disabled]="!selectedFile || resumeForm.invalid || isSubmitting"
                >
                  {{ isSubmitting ? 'Uploading…' : 'Upload resume' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      <!-- RIGHT: SUBMIT RESUME / JOB TARGETING CARD --------------------- -->
      <form
        class="submitresume"
        [formGroup]="enhanceForm"
        (ngSubmit)="submitResume()"
      >
        <div *ngIf="!selectedResume" class="preview-empty">
          <h3>Target a job</h3>
          <p>
            Select a resume card on the left, then describe the job and role to
            generate a tailored version.
          </p>
        </div>

        <div *ngIf="selectedResume" class="preview-content">
          <div class="preview-header">
            <div>
              <h3>
                Targeting:
                {{ selectedResume.title || 'Untitled resume' }}
              </h3>
              <p class="preview-category">
                {{ selectedResume.primaryCategory || 'Uncategorized' }}
              </p>
            </div>

            <button
              type="button"
              class="icon-button"
              title="Clear selection"
              (click)="clearPreview()"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="preview-meta">
            <span>
              Created: {{ selectedResume.createdAt | date: 'short' }}
            </span>
            <span>
              Type: {{ selectedResume.fileType | uppercase }}
            </span>
          </div>

          <div class="preview-section">
            <h4>Job title</h4>
            <input
              type="text"
              formControlName="jobTitle"
              placeholder="e.g. Senior AI Engineer"
            />
          </div>

          <div class="preview-section">
            <h4>Job description</h4>
            <textarea
              formControlName="jobDescription"
              rows="5"
              placeholder="Paste the job description here…"
            ></textarea>
          </div>

          <div class="preview-section">
            <h4>Job role label</h4>
            <select formControlName="jobRoleLabel">
              <option *ngFor="let label of jobRoleLabels" [value]="label">
                {{ label }}
              </option>
            </select>
          </div>

          <div class="preview-section">
            <h4>Output format</h4>
            <select formControlName="outputFormat">
              <option value="docx">DOCX</option>
              <option value="pdf">PDF</option>
              <option value="gdoc">Google Doc</option>
            </select>
          </div>

          <div class="preview-section" *ngIf="enhanceError">
            <p class="preview-status">
              {{ enhanceError }}
            </p>
          </div>

          <div class="preview-actions">
            <button
              type="submit"
              class="text-button bigrolebutton"
              [disabled]="
                !selectedResume ||
                selectedResume.status === 'enhancing' ||
                !selectedResume.fileUrl ||
                enhanceForm.invalid ||
                isEnhancing
              "
            >
              {{ isEnhancing ? 'Enhancing…' : 'Submit resume for this job' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </section>

  <!-- MAIN LAYOUT: LISTS + PREVIEW ------------------------------------- -->
  <div class="content">
    <div class="content-layout">
      <!-- LEFT: PINNED + OTHERS ---------------------------------------- -->
      <div class="content-main">
        <!-- PINNED RESUMES ---------------------------------------------- -->
        <section class="resume-section" *ngIf="filteredPinned.length">
          <div class="section__header">
            <h3 class="resume-section__title">Pinned resumes</h3>
            <span class="section__count">{{ filteredPinned.length }}</span>
          </div>

          <div
            cdkDropList
            [id]="pinnedListId"
            [cdkDropListData]="pinnedResumes"
            [cdkDropListConnectedTo]="[otherListId]"
            (cdkDropListDropped)="dropPinned($event)"
            class="resume-grid"
          >
            <article
              class="resume-card"
              *ngFor="let item of filteredPinned"
              cdkDrag
              (click)="selectResume(item)"
              [style.backgroundColor]="item.color || defaultCardColor"
            >
              <header class="resume-card__header">
                <div class="resume-card__title-block">
                  <h4 class="resume-title">
                    {{ item.title || 'Untitled resume' }}
                  </h4>
                  <span class="resume-category">
                    {{ item.primaryCategory || 'Uncategorized' }}
                  </span>
                </div>

                <div class="resume-card__header-actions">
                  <button
                    type="button"
                    class="icon-button"
                    title="Unpin"
                    (click)="togglePinned(item, false); $event.stopPropagation()"
                  >
                    <i class="fa-solid fa-thumbtack"></i>
                  </button>
                  <button
                    type="button"
                    class="icon-button"
                    title="Archive / restore"
                    (click)="archive(item); $event.stopPropagation()"
                  >
                    <i
                      class="fa-solid"
                      [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"
                    >
                    </i>
                  </button>
                </div>
              </header>

              <div class="resume-card__body">
                <p class="resume-description" *ngIf="item.description">
                  {{ item.description }}
                </p>

                <div class="resume-labels" *ngIf="item.labels?.length">
                  <span
                    class="label-chip"
                    *ngFor="let lbl of item.labels"
                    (click)="selectLabel(lbl); $event.stopPropagation()"
                    [class.active]="lbl === selectedLabel"
                  >
                    {{ lbl }}
                  </span>
                </div>

                <div class="enhanced-snippet" *ngIf="item.enhancedText">
                  <span class="enhanced-tag">Enhanced</span>
                  <p>
                    {{ item.enhancedText | slice: 0:200 }}
                    <span *ngIf="item.enhancedText.length > 200">…</span>
                  </p>
                </div>
              </div>

              <footer class="resume-card__footer">
                <div class="resume-meta">
                  <span class="date">
                    {{ item.createdAt | date: 'short' }}
                  </span>
                  <span class="file-type">
                    {{ item.fileType | uppercase }}
                  </span>
                </div>

                <div class="resume-actions">
                  <button
                    type="button"
                    class="text-button"
                    (click)="openResume(item); $event.stopPropagation()"
                  >
                    Open resume
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="openResumeDialog(item); $event.stopPropagation()"
                  >
                    Preview dialog
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="enhanceForJob(item); $event.stopPropagation()"
                    [disabled]="item.status === 'enhancing' || !item.fileUrl"
                  >
                    Enhance for job
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="useInChat(item); $event.stopPropagation()"
                  >
                    Use in chat
                  </button>

                  <div class="color-dots">
                    <button
                      type="button"
                      *ngFor="let c of colorPalette"
                      class="color-dot"
                      [style.backgroundColor]="c"
                      [class.selected]="item.color === c"
                      (click)="changeColor(item, c); $event.stopPropagation()"
                    >
                    </button>
                  </div>

                  <button
                    type="button"
                    class="text-button text-button--danger"
                    (click)="delete(item); $event.stopPropagation()"
                  >
                    Delete
                  </button>
                </div>
              </footer>
            </article>
          </div>
        </section>

        <!-- OTHER RESUMES ----------------------------------------------- -->
        <section class="resume-section" *ngIf="filteredOthers.length">
          <div class="section__header">
            <h3 class="resume-section__title">All resumes</h3>
            <span class="section__count">{{ filteredOthers.length }}</span>
          </div>

          <div
            cdkDropList
            [id]="otherListId"
            [cdkDropListData]="otherResumes"
            [cdkDropListConnectedTo]="[pinnedListId]"
            (cdkDropListDropped)="dropOthers($event)"
            class="resume-grid"
          >
            <article
              class="resume-card"
              *ngFor="let item of filteredOthers"
              cdkDrag
              (click)="selectResume(item)"
              [style.backgroundColor]="item.color || defaultCardColor"
            >
              <header class="resume-card__header">
                <div class="resume-card__title-block">
                  <h4 class="resume-title">
                    {{ item.title || 'Untitled resume' }}
                  </h4>
                  <span class="resume-category">
                    {{ item.primaryCategory || 'Uncategorized' }}
                  </span>
                </div>

                <div class="resume-card__header-actions">
                  <button
                    type="button"
                    class="icon-button"
                    title="Pin"
                    (click)="togglePinned(item, true); $event.stopPropagation()"
                  >
                    <i class="fa-regular fa-thumbtack"></i>
                  </button>
                  <button
                    type="button"
                    class="icon-button"
                    title="Archive / restore"
                    (click)="archive(item); $event.stopPropagation()"
                  >
                    <i
                      class="fa-solid"
                      [ngClass]="item.archived ? 'fa-box-open' : 'fa-box-archive'"
                    >
                    </i>
                  </button>
                </div>
              </header>

              <div class="resume-card__body">
                <p class="resume-description" *ngIf="item.description">
                  {{ item.description }}
                </p>

                <div class="resume-labels" *ngIf="item.labels?.length">
                  <span
                    class="label-chip"
                    *ngFor="let lbl of item.labels"
                    (click)="selectLabel(lbl); $event.stopPropagation()"
                    [class.active]="lbl === selectedLabel"
                  >
                    {{ lbl }}
                  </span>
                </div>

                <div class="enhanced-snippet" *ngIf="item.enhancedText">
                  <span class="enhanced-tag">Enhanced</span>
                  <p>
                    {{ item.enhancedText | slice: 0:200 }}
                    <span *ngIf="item.enhancedText.length > 200">…</span>
                  </p>
                </div>
              </div>

              <footer class="resume-card__footer">
                <div class="resume-meta">
                  <span class="date">
                    {{ item.createdAt | date: 'short' }}
                  </span>
                  <span class="file-type">
                    {{ item.fileType | uppercase }}
                  </span>
                </div>

                <div class="resume-actions">
                  <button
                    type="button"
                    class="text-button"
                    (click)="openResume(item); $event.stopPropagation()"
                  >
                    Open resume
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="openResumeDialog(item); $event.stopPropagation()"
                  >
                    Preview dialog
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="enhanceForJob(item); $event.stopPropagation()"
                    [disabled]="item.status === 'enhancing' || !item.fileUrl"
                  >
                    Enhance for job
                  </button>

                  <button
                    type="button"
                    class="text-button"
                    (click)="useInChat(item); $event.stopPropagation()"
                  >
                    Use in chat
                  </button>

                  <div class="color-dots">
                    <button
                      type="button"
                      *ngFor="let c of colorPalette"
                      class="color-dot"
                      [style.backgroundColor]="c"
                      [class.selected]="item.color === c"
                      (click)="changeColor(item, c); $event.stopPropagation()"
                    >
                    </button>
                  </div>

                  <button
                    type="button"
                    class="text-button text-button--danger"
                    (click)="delete(item); $event.stopPropagation()"
                  >
                    Delete
                  </button>
                </div>
              </footer>
            </article>
          </div>
        </section>
      </div>

      <!-- RIGHT: PREVIEW PANE ------------------------------------------ -->
      <aside class="preview-pane">
        <div *ngIf="!selectedResume" class="preview-empty">
          <h3>Resume preview</h3>
          <p>Select a card on the left to see the full resume details here.</p>
        </div>

        <div *ngIf="selectedResume" class="preview-content">
          <div class="preview-header">
            <div>
              <h3>{{ selectedResume.title || 'Untitled resume' }}</h3>
              <p class="preview-category">
                {{ selectedResume.primaryCategory || 'Uncategorized' }}
              </p>
            </div>

            <button
              type="button"
              class="icon-button"
              title="Clear preview"
              (click)="clearPreview()"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="preview-meta">
            <span>
              Created: {{ selectedResume.createdAt | date: 'short' }}
            </span>
            <span>
              Type: {{ selectedResume.fileType | uppercase }}
            </span>
          </div>

          <div class="preview-labels" *ngIf="selectedResume.labels?.length">
            <span
              class="label-chip"
              *ngFor="let lbl of selectedResume.labels"
              (click)="selectLabel(lbl)"
              [class.active]="lbl === selectedLabel"
            >
              {{ lbl }}
            </span>
          </div>

          <div class="preview-section" *ngIf="selectedResume.description">
            <h4>Summary / notes</h4>
            <p>{{ selectedResume.description }}</p>
          </div>

          <div class="preview-section">
            <h4>Enhanced resume</h4>
            <div
              *ngIf="selectedResume.status === 'enhancing'"
              class="preview-status"
            >
              Enhancing resume for the last job request…
            </div>
            <pre *ngIf="selectedResume.enhancedText" class="preview-body">
{{ selectedResume.enhancedText }}
            </pre>
            <p
              *ngIf="
                !selectedResume.enhancedText &&
                selectedResume.status !== 'enhancing'
              "
            >
              No enhanced version yet. Use the
              <strong>“Target a job”</strong>
              card above to generate one.
            </p>
          </div>

          <div class="preview-actions">
            <button
              type="button"
              class="text-button"
              (click)="openResume(selectedResume)"
              [disabled]="!selectedResume.fileUrl"
            >
              Open resume file
            </button>
            <button
              type="button"
              class="text-button"
              (click)="enhanceForJob(selectedResume)"
              [disabled]="
                selectedResume.status === 'enhancing' || !selectedResume.fileUrl
              "
            >
              Enhance for job
            </button>
            <button
              type="button"
              class="text-button"
              (click)="useInChat(selectedResume)"
            >
              Send summary to chat
            </button>
          </div>
        </div>
      </aside>
    </div>
  </div>
</div>
